import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { formatBalance, formatTRY } from "@/lib/format";
import { EmptyRow, LoadingRow, PageHeader, Pager } from "@/components/common/States";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

import { IL_NONE, IL_OPTIONS } from "@/lib/iller";
import CustomerDialog from "@/components/customers/CustomerDialog";
import ImportWizard from "@/components/import/ImportWizard";
import CreditBalanceView from "@/components/customers/CreditBalanceView";
import { useFeatures } from "@/lib/features";
import { TestBadge } from "@/components/common/TestBadge";

const PAGE_SIZE = 25;
const TYPE_STYLE = { Toptan: "bg-blue-50 text-blue-700 border-blue-200", Bayi: "bg-indigo-50 text-indigo-700 border-indigo-200", Perakende: "bg-slate-100 text-slate-700 border-slate-200" };

const CustomerRow = ({ c, onOpen, onSell, sel, onSel }) => (
  <tr data-testid={`customer-row-${c.code}`} onClick={() => onOpen(c.id)} className="cursor-pointer">
    {onSel && <td onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={sel} onChange={() => onSel(c.id)} data-testid={`customer-select-${c.code}`} /></td>}
    <td className="font-mono text-xs font-medium text-slate-900">{c.code}</td>
    <td className="max-w-[260px] truncate font-medium text-slate-800" title={c.title}>{c.title}<TestBadge show={c.is_test} testid={`customer-test-badge-${c.code}`} /></td>
    <td><span className={`text-[11px] px-1.5 py-0.5 rounded border ${TYPE_STYLE[c.customer_type]}`}>{c.customer_type}</span></td>
    <td>{c.city || <span className="text-slate-400 italic">İl belirtilmemiş</span>}{c.district && <span className="text-slate-400"> / {c.district}</span>}</td>
    <td>{c.sales_rep_name || <span className="text-slate-400 italic">Atanmamış</span>}</td>
    <td className="text-right font-mono font-semibold" data-testid={`customer-risk-${c.code}`}>{formatTRY(c.risk_limit)}</td>
    <td className="text-right font-mono">{c.vade_days} gün</td>
    <td className="text-right font-mono" data-testid={`customer-balance-${c.code}`}>{formatBalance(c.balance)}</td>
    <td className={`text-right font-mono ${c.overdue_debt > 0 ? "text-rose-600 font-semibold" : "text-slate-400"}`}>{formatTRY(c.overdue_debt)}</td>
    <td>{c.contact_name}</td>
    <td className="font-mono text-xs">{c.phone}</td>
    <td className="font-mono text-xs text-slate-500">{[c.tax_office, c.tax_no].filter(Boolean).join(" · ") || "—"}</td>
    {onSell && <td><button onClick={(e) => { e.stopPropagation(); onSell(c.id); }} className="text-[11px] px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap" data-testid={`customer-sell-${c.code}`}>Satış Yap</button></td>}
  </tr>
);

export default function Customers() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [view, setView] = useState("all");
  const [reload, setReload] = useState(0);
  const [q, setQ] = useState("");
  const [rep, setRep] = useState("all");
  const [reps, setReps] = useState([]);
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);
  const [city, setCity] = useState("");
  const [sel, setSel] = useState(new Set());
  const [il, setIl] = useState(null);
  const onSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const assignIl = async () => {
    try {
      const { data } = await api.post("/customers/bulk-il", { customer_ids: [...sel], il: il === IL_NONE ? "" : il });
      toast.success(`${data.updated} cari güncellendi${data.unchanged ? ` · ${data.unchanged} değişmedi` : ""}${data.failed ? ` · ${data.failed} hata` : ""}`);
      setSel(new Set()); setIl(null); setReload((r) => r + 1);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { api.get("/customers/sales-reps").then(({ data }) => setReps(data)); }, []);
  useEffect(() => {
    setRes(null);
    const t = setTimeout(() => {
      api.get("/customers", { params: { q, city, sales_rep_id: rep === "all" ? "" : rep, page, page_size: PAGE_SIZE } }).then(({ data }) => setRes(data));
    }, 250);
    return () => clearTimeout(t);
  }, [q, rep, page, reload, city]);

  const tek = useFeatures().tek_cari;
  const canEdit = can("cariler", "edit");
  return (
    <div data-testid="customers-page">
      <PageHeader title="Cariler" subtitle={`Müşteri kartları, risk limitleri ve vade koşulları${res?.scope === "own" ? " · yalnızca kendi carileriniz" : ""}`} testid="customers-title">
        {can("cariler", "create") && <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setCreating(true)} data-testid="customer-create-button"><Plus className="h-4 w-4 mr-1" />Yeni Cari</Button>}
        {can("cariler", "edit") && <Button size="sm" variant="outline" onClick={() => setBulk(true)} data-testid="customer-bulk-update-button">Excel ile Toplu Güncelle</Button>}
      </PageHeader>
      {creating && <CustomerDialog onClose={() => setCreating(false)} onSaved={(c) => navigate(`/cariler/${c.id}`)} />}
      {bulk && <ImportWizard importer="cariler" title="Cariler · Excel ile Toplu Güncelle" onClose={() => setBulk(false)} onDone={() => setReload((r) => r + 1)} />}
      {!tek && <div className="flex gap-1 mb-3" data-testid="customer-view-toggle">
        <Button size="sm" variant={view === "all" ? "default" : "outline"} onClick={() => setView("all")} data-testid="customer-view-all">Tüm cariler</Button>
        <Button size="sm" variant={view === "credit" ? "default" : "outline"} onClick={() => setView("credit")} data-testid="customer-view-credit">Alacak bakiyeli cariler</Button>
      </div>}
      {view === "credit" && !tek ? <CreditBalanceView /> : (
      <div className="panel overflow-hidden">
        <div className="flex flex-col sm:flex-row gap-2 p-3 border-b border-slate-200">
          <div className="relative flex-1 max-w-sm">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-8 h-9" placeholder="Unvan, cari kodu, vergi no..." value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="customer-search-input" />
          </div>
          <Select value={rep} onValueChange={(v) => { setRep(v); setPage(1); }}>
            <SelectTrigger className="h-9 sm:w-56" data-testid="customer-rep-select"><SelectValue placeholder="Temsilci" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm temsilciler</SelectItem>
              {reps.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <select value={city} onChange={(e) => { setCity(e.target.value); setPage(1); }} className="h-9 sm:w-48 rounded-md border border-input bg-white px-2 text-sm" data-testid="customer-city-filter">
            <option value="">Tüm iller</option>{IL_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          {canEdit && sel.size > 0 && <div className="flex items-center gap-2 ml-auto" data-testid="customer-bulk-il-bar">
            <span className="text-[13px] text-slate-600">{sel.size} cari seçili</span>
            <select value={il ?? ""} onChange={(e) => setIl(e.target.value || null)} className="h-9 w-44 rounded-md border border-input bg-white px-2 text-sm" data-testid="customer-bulk-il-select">
              <option value="">İl seçin…</option>{IL_OPTIONS.map(([v, l]) => <option key={v} value={v}>{v === IL_NONE ? "İl bilgisini temizle" : l}</option>)}</select>
            <Button size="sm" disabled={!il} onClick={assignIl} data-testid="customer-bulk-il-apply">İl Ata</Button>
            <Button size="sm" variant="outline" onClick={() => setSel(new Set())} data-testid="customer-bulk-il-clear">Seçimi temizle</Button></div>}
        </div>
        <div className="overflow-x-auto max-h-[calc(100vh-260px)]">
          <table className="erp-table" data-testid="customers-table">
            <thead>
              <tr>{canEdit && <th><input type="checkbox" checked={!!res?.items.length && res.items.every((c) => sel.has(c.id))} onChange={() => setSel((s) => { const n = new Set(s); const all = res.items.every((c) => n.has(c.id)); res.items.forEach((c) => (all ? n.delete(c.id) : n.add(c.id))); return n; })} title="Sayfadakileri seç" data-testid="customer-select-page" /></th>}<th>Kod</th><th>Unvan</th><th>Tip</th><th>İl / İlçe</th><th>Temsilci</th><th className="text-right">Risk Limiti</th><th className="text-right">Vade</th><th className="text-right">Bakiye</th><th className="text-right">Vadesi Geçmiş</th><th>Yetkili</th><th>Telefon</th><th>Vergi D. / No</th>{can("hizli_satis", "create") && <th></th>}</tr>
            </thead>
            <tbody>
              {!res ? <LoadingRow cols={canEdit ? 13 : 12} /> : res.items.length === 0 ? <EmptyRow cols={canEdit ? 13 : 12} /> : res.items.map((c) => <CustomerRow key={c.id} c={c} sel={sel.has(c.id)} onSel={canEdit ? onSel : null} onOpen={(id) => navigate(`/cariler/${id}`)} onSell={can("hizli_satis", "create") ? (id) => navigate(`/hizli-satis?customer=${id}`) : null} />)}
            </tbody>
          </table>
        </div>
        {res && <Pager page={page} pageSize={PAGE_SIZE} total={res.total} onPage={setPage} testid="customers-pager" />}
      </div>)}
    </div>
  );
}
